import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useColorTheme } from '../context/ColorThemeContext';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const CreditBalance = ({ onPurchaseClick, minimal = false, sessionToken }) => {
  const { currentColors, colorTheme } = useColorTheme();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBalance = async () => {
    try {
      if (!sessionToken) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/api/payments/user/balance`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setBalance(response.data);
    } catch (error) {
      console.error('Error fetching credit balance:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessionToken) {
      fetchBalance();
      // Refresh every 30 seconds
      const interval = setInterval(fetchBalance, 30000);
      return () => clearInterval(interval);
    }
  }, [sessionToken]);

  if (loading || !balance) return null;

  // Power users don't need credits
  if (balance.is_power_user) {
    return minimal ? null : (
      <div className={`inline-flex items-center px-3 py-1.5 rounded-full ${
        colorTheme === 'purple' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
        colorTheme === 'blue' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
        'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      }`}>
        <span className="mr-1">⚡</span>
        <span className="text-sm font-semibold">Power User</span>
      </div>
    );
  }

  if (minimal) {
    // Compact view for header
    return (
      <button
        onClick={onPurchaseClick}
        className={`inline-flex items-center px-3 py-1.5 rounded-full transition-all ${
          balance.credits > 10
            ? colorTheme === 'purple' ? 'bg-purple-100 text-purple-800 hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-200' :
              colorTheme === 'blue' ? 'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200' :
              'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200'
            : 'bg-orange-100 text-orange-800 hover:bg-orange-200 dark:bg-orange-900 dark:text-orange-200'
        }`}
        title="Click to purchase credits"
      >
        <span className="mr-1">💳</span>
        <span className="text-sm font-semibold">{balance.credits}</span>
        <span className="ml-1 text-xs">credits</span>
      </button>
    );
  }

  // Full card view for dashboard
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Credit Balance</h3>
        <span className="text-3xl">💳</span>
      </div>

      <div className="mb-4">
        <div className="flex items-baseline">
          <span className={`text-4xl font-bold ${
            balance.credits > 10 ? 'text-gray-900 dark:text-white' : 'text-orange-600 dark:text-orange-400'
          }`}>
            {balance.credits}
          </span>
          <span className="ml-2 text-gray-500 dark:text-gray-400">credits</span>
        </div>
        {balance.credits <= 10 && (
          <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
            ⚠️ Running low on credits
          </p>
        )}
      </div>

      <div className="space-y-2 mb-4 text-sm">
        <div className="flex justify-between text-gray-600 dark:text-gray-400">
          <span>Total Purchased:</span>
          <span className="font-medium text-gray-900 dark:text-white">{balance.total_purchased}</span>
        </div>
        <div className="flex justify-between text-gray-600 dark:text-gray-400">
          <span>Total Spent:</span>
          <span className="font-medium text-gray-900 dark:text-white">{balance.total_spent}</span>
        </div>
        <div className="flex justify-between text-gray-600 dark:text-gray-400">
          <span>Free Tier Used:</span>
          <span className="font-medium text-gray-900 dark:text-white">
            {balance.free_tier_used}/{balance.free_tier_limit}
          </span>
        </div>
      </div>

      <button
        onClick={onPurchaseClick}
        className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
          colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700 text-white' :
          colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700 text-white' :
          'bg-green-600 hover:bg-green-700 text-white'
        }`}
      >
        Purchase Credits
      </button>
    </div>
  );
};

export default CreditBalance;
